import { $service } from '@xt/client/experimental/request/service'
import { noop } from 'lodash'
import { BindAll } from 'lodash-decorators'
import { action, observable, toJS } from 'mobx'
import { hash } from 'ohash'
import lodash from 'lodash'

export enum EnumLoadResult {
  /**
   * 异常取消
   */
  Cancel,
  /**
   * 接口调用失败
   */
  Error,
  /**
   * 数据加载成功
   */
  Loaded,
  /**
   * 没有下一页数据了
   */
  Complete
}

// 请求原始配置
export type PageMeta<T> = {
  inited: boolean
  isLoading: boolean
  page: number
  pageSize: number
  lastId?: string | number
  hasMore: boolean
  url?: string
  mapValues?: (res: any) => any
}

// 缓存数据结构
export type DataCache<T> = { data: T[]; status: PageMeta<any> }

// 缓存数据map
export type DataCacheMap<T> = Map<string, DataCache<T>>

// 初始化状态记录
export type InitStatus = {
  inited: boolean
  page: number
}

@BindAll()
export class PagePagination<T = any> {
  @observable identifier: string = ''
  @observable dataSource: Array<T> = []
  @observable pageMeta: PageMeta<T> = {
    inited: false,
    isLoading: false,
    hasMore: true,
    page: 0,
    pageSize: 10,
    lastId: undefined,
    url: '',
    mapValues: noop
  }
  /**
   * @description 列表额外拓展请求参数
   * @type Record<string, unknown> | null
   * @default null
   */
  @observable body: Record<string, unknown> | null = null

  @observable cacheList: DataCacheMap<T> | null = null
  @observable initStatus: Record<string, InitStatus> | null = null

  // lastId的数据路径
  lastIdPath: string
  lastParams: string = 'lastId'
  needLastId: boolean
  needCache: boolean = false

  get lastId() {
    return lodash.get(lodash.last(this.dataSource), this.lastIdPath || 'id')
  }

  // 构造函数结构配置
  constructor(options) {
    const { url, mapValues, pageSize = 10, needCache, lastIdPath, needLastId = true } = options

    this.setPageMeta({
      url,
      mapValues,
      pageSize
    })
    if (needCache) {
      this.needCache = needCache
      this.cacheList = new Map()
      this.initStatus = {}
    }
    this.lastIdPath = lastIdPath
    this.needLastId = needLastId
  }

  public loadData(body?) {
    // 每次更新body值
    if (body) {
      this.setBody(body)
    }

    if (this.needCache) {
      const { page } = this.pageMeta
      const cache = this.getCache({
        page,
        ...this.body
      })

      if (cache) {
        console.warn('loading-命中缓存')
        this.setDataSource(cache.data)
        // 命中缓存之后，通过page数加一实现当前tabs继续滚动加载事件
        this.setPageMeta({
          page: page + 1
        })

        // 缓存数据没有更多的情况
        if (cache.status.hasMore === false) {
          return EnumLoadResult.Complete
        }

        return EnumLoadResult.Loaded
      } else {
        console.warn('loading-未命中缓存')
        return this.fetchData()
      }
    } else {
      return this.fetchData()
    }
  }

  // 请求接口
  private async fetchData() {
    const { isLoading, page, pageSize, hasMore, inited, lastId, url, mapValues } = this.pageMeta

    if (isLoading) return EnumLoadResult.Cancel

    if (!hasMore) return EnumLoadResult.Complete

    try {
      this.setPageMeta({
        isLoading: true
      })

      const params = {
        page: page + 1,
        pageSize,
        ...this.body
      }

      lodash.set(params, this.lastParams, this.needLastId ? lastId : undefined)

      const [err, res] = await $service({
        method: 'GET',
        url,
        data: params,
        params
      })

      if (err) return EnumLoadResult.Error

      const { dataSource: data } = mapValues ? mapValues(res) : { dataSource: res }

      if (!inited) {
        this.setDataSource(data)
      } else {
        this.appendData(data)
      }

      this.setPageMeta({
        inited: true,
        page: page + 1,
        hasMore: data.length === pageSize,
        lastId: data.length ? this.lastId : lastId
      })

      if (this.needCache) {
        // 保证同一页铺不够，连续拉取能够走到下一页接口
        this.setCache(
          toJS(this.dataSource),
          hash({
            page,
            ...this.body
          })
        )
        this.setInitStatus(
          {
            page,
            inited: true
          },
          hash({
            ...this.body
          })
        )
      }

      return data.length === pageSize ? EnumLoadResult.Loaded : EnumLoadResult.Complete
    } catch (err) {
      console.log(err)
    } finally {
      this.setPageMeta({
        isLoading: false
      })
    }
  }

  // 追加数据
  @action
  private appendData(data) {
    this.dataSource = [...this.dataSource, ...data]
  }

  // 在两个tabs存在同一条数据的情况下，数据操作之后要同步其他tabs中对应的相同数据，避免重复操作出现异常
  @action
  private syncCacheData(updateCallback: (dataSource: Array<T>) => Array<T>) {
    if (!this.cacheList) return
    this.cacheList.forEach((value, key) => {
      const newCache = updateCallback(toJS(value.data))
      this.cacheList!.set(key, { ...value, data: newCache })
    })
  }

  // 更新数据源，同时更新缓存
  @action
  public updateSource(data, updateCallback?: (dataSource: Array<T>) => Array<T>) {
    this.dataSource = data

    //TODO: 更新缓存数据 - （仅作展示回显用）
    if (this.needCache) {
      const cacheStatus =
        this.initStatus?.[
          hash({
            ...this.body
          })
        ]

      if (updateCallback) {
        this.syncCacheData(updateCallback)
      } else {
        this.setCache(
          toJS(this.dataSource),
          hash({
            page: cacheStatus?.page,
            ...this.body
          })
        )
      }
    }
  }

  // 设置数据源
  @action
  private setDataSource(data) {
    this.dataSource = data
  }

  // 设置page状态
  @action
  private setPageMeta(status: Partial<PageMeta<any>>) {
    this.pageMeta = {
      ...this.pageMeta,
      ...status
    }
  }

  @action
  private setCache(data: Array<T>, cacehKey: string) {
    const { page } = this.pageMeta
    this.cacheList?.set(cacehKey, {
      data,
      status: {
        ...this.pageMeta,
        page: page - 1, // 缓存时，page - 1
        isLoading: false
      }
    })
  }

  @action
  private getCache(params: Record<string, unknown>): DataCache<T> | undefined {
    const cacehKey = hash({
      ...params
    })

    if (this.cacheList?.get(cacehKey)) {
      return this.cacheList.get(cacehKey)
    }
  }

  @action
  private setBody(body: Record<string, unknown>) {
    this.body = body
  }

  @action
  private setInitStatus(data: InitStatus, cacehKey: string) {
    this.initStatus && (this.initStatus[cacehKey] = data)
  }

  @action
  private updateIdentifier(identifier: string) {
    this.identifier = identifier
  }

  @action
  public clearData(body?: Record<string, unknown>) {
    // 使用缓存的情况下切换使用实时body值
    if (body) {
      this.setBody(body)
    }
    // 判断是否缓存过
    const cacheStatus =
      this.initStatus?.[
        hash({
          ...this.body
        })
      ]

    if (cacheStatus?.inited && this.needCache) {
      // 获取缓存页码，对应body下的page值
      const { page } = cacheStatus
      const cache = this.getCache({
        page,
        ...this.body
      })

      // 缓存存在的时候，还原参数
      if (cache) {
        this.setPageMeta({
          ...cache.status
        })
      }
    } else {
      // 还原初始状态
      this.setPageMeta({
        inited: false,
        isLoading: false,
        hasMore: true,
        page: 0,
        lastId: undefined
      })
    }

    this.setDataSource([])
    // 重置identifier，根据时间戳随机生成避免infinite-loading组件不触发加载
    this.compReset((+new Date()).toString(36))
  }

  // 重置所有属性
  @action
  public reset() {
    this.dataSource = []
    this.body = null
    this.setPageMeta({
      inited: false,
      isLoading: false,
      hasMore: true,
      page: 0,
      lastId: undefined
    })
    this.cacheList = new Map()
    this.initStatus = {}
    this.identifier = ''
  }

  // 重置组件状态
  public compReset(identifier: string) {
    this.updateIdentifier(identifier)
  }
}

export default PagePagination
